import {
    Datagrid,
    List,
    Show,
    SimpleShowLayout,
    TextField,
    Pagination,
    useNotify,
    useRefresh,
    Confirm,
    useRecordContext,
} from "react-admin";
import { RelativeDateTimeField, ShortDateField, ConfirmOnRecordButton } from "./customFields";
import { BulkActionButtons } from "./common";
import { useState } from "react";
import { httpClient } from "./dataProvider";
import ActionCheck from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import { Button } from "@mui/material";

const StartBackgroundTaskButton = (props: {
    type: string;
    title: string;
    content: string;
    buttonText: string;
}) => {
    const notify = useNotify();
    const [isLoading, setLoading] = useState(false);
    const [open, setOpen] = useState(false);
    const refresh = useRefresh();

    const handleClick = () => setOpen(true);
    const handleDialogClose = () => setOpen(false);
    const handleConfirm = () => {
        const body = JSON.stringify({ type: props.type });

        httpClient(import.meta.env.VITE_SERVER_URL + `/cms/background_processings/start`, {
            method: "POST",
            body: body,
        })
            .then(() => {
                setLoading(false);
                setOpen(false);
                refresh();
            })
            .catch((error) => {
                notify(error.toString(), { type: "error" });
                setLoading(false);
                setOpen(false);
            });
    };

    return (
        <>
            <Confirm
                isOpen={open}
                loading={isLoading}
                title={props.title}
                content={props.content}
                onConfirm={handleConfirm}
                onClose={handleDialogClose}
                ConfirmIcon={ActionCheck}
                CancelIcon={CancelIcon}
            />
            <Button
                onClick={handleClick}
                disabled={isLoading}
                variant="contained"
                color="primary"
                sx={{ width: "400px", margin: "10px" }}
            >
                {props.buttonText}
            </Button>
        </>
    );
};

const CancelButton = () => {
    const record = useRecordContext();

    if (!record || record.status !== "in_progress") return null;

    return (
        <ConfirmOnRecordButton
            confirmTitle={`Cancel Task #${record.id}`}
            confirmBody="Are you sure you want to cancel this task?"
            url="/cms/background_processing/{id}/cancel"
            text="Cancel"
        />
    );
};

export const BackgroundProcessingList = () => (
    <>
        <StartBackgroundTaskButton
            type="reprocess_outdated_ddex_messages"
            title="Reprocess outdated DDEX messages"
            content="Are you sure you want to reprocess outdated DDEX messages?"
            buttonText="Reprocess outdated DDEX messages"
        />
        <StartBackgroundTaskButton
            type="fetch_viberate_stats"
            title="Fetch all Viberate stats"
            content="Are you sure you want to start fetching all Viberate stats?"
            buttonText="Fetch Viberate stats"
        />
        <StartBackgroundTaskButton
            type="fix_missing_ddex_messages"
            title="Fix missing ddex messages"
            content="Are you sure you want to start fix missing ddex message task?"
            buttonText="Fix missing DDEX messages"
        />
        <StartBackgroundTaskButton
            type="reprocess_failed_ddex_messages"
            title="Reprocess failed ddex messages"
            content="Are you sure you want to reprocess failed DDEX messages?"
            buttonText="Reprocess failed DDEX messages"
        />
        <StartBackgroundTaskButton
            type="archive_all_ddex_medias"
            title="Archive all DDEX medias"
            content="Are you sure you want to archive all DDEX medias?"
            buttonText="Archive all DDEX medias"
        />
        <StartBackgroundTaskButton
            type="refresh_top_viberate_track_stats"
            title="Refresh top Viberate track stats"
            content="Are you sure you want to refresh top Viberate track stats?"
            buttonText="Refresh top Viberate track stats"
        />
        <StartBackgroundTaskButton
            type="fetch_viberate_top_chart"
            title="Fetch Viberate top chart"
            content="Are you sure you want to fetch Viberate top chart?"
            buttonText="Fetch Viberate top chart"
        />
        <List
            perPage={50}
            pagination={<Pagination rowsPerPageOptions={[25, 50, 100, 200]} />}
            sort={{ field: "created_at", order: "DESC" }}
        >
            <Datagrid rowClick="show" bulkActionButtons={<BulkActionButtons />}>
                <TextField source="id" />
                <TextField source="name" />
                <TextField source="status" />
                <TextField source="progress" />
                <ShortDateField source="created_at" label="Added on" />
            </Datagrid>
        </List>
    </>
);

export const BackgroundProcessingShow = () => (
    <Show>
        <SimpleShowLayout>
            <h2>BackgroundProcessing</h2>
            <TextField source="id" />
            <TextField source="name" />
            <TextField source="status" />
            <TextField source="progress" />
            <TextField source="current" />
            <TextField source="total" />
            <RelativeDateTimeField source="created_at" />
            <CancelButton />
        </SimpleShowLayout>
    </Show>
);
